<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { location, push, replace } from 'svelte-spa-router';
	//Helpers
	import {
		addDashboard,
		deleteDashboard,
		checkIsEventLogTab,
		requestChangeDashboardName,
	} from '../list-helpers';
	import { getBackgroundColorPreviewImages } from 'src/helpers/background_color_helpers';
	// Stores
	import { dashboards, dashboardTabs } from 'src/stores/features/dashboards';
	import { user_data, user_settings } from 'src/stores';
	import { NON_PREMIUM_DASH_TABS_LIMIT, PREMIUM_DASH_TABS_LIMIT } from 'src/constants';
	// Components
	import { Button, Modal, Text, Container, Input, Icon, Alert } from '@Shelly/shelly-design-system';
	import ImageList from '../ImageListV2.svelte';
	// Types
	import type { TypeTab } from '../../../types/Tabs';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		createNewDashWithDefaultWidgets,
		DEFAULT_DASH_ID,
		mapDashToAvatar,
	} from 'src/components/Dashboards/dash-helpers';
	import { createAsyncAction } from 'src/helpers/async_action_guard';

	const dispatch = createEventDispatcher();
	const isPremium = $user_data.premium || $user_settings.been_premium;
	const DASHBOARDS_LIMIT = isPremium ? PREMIUM_DASH_TABS_LIMIT : NON_PREMIUM_DASH_TABS_LIMIT;
	const backgroundColorPreviewImages = getBackgroundColorPreviewImages();
	const DEFAULT_DASH_ICON = 'fat fa-objects-column';

	const alertMessegaLimitTranslation = isPremium
		? 'alert_premium_dashboard_limit_reached'
		: 'alert_basic_dashboard_limit_reached';

	const alertMessageReachedLimitDefault = isPremium
		? `Dashboard limit of ${PREMIUM_DASH_TABS_LIMIT} reached! Remove some of your dashboards to add a new one.`
		: `Dashboard limit of ${NON_PREMIUM_DASH_TABS_LIMIT} reached! Upgrade to Premium to create up to ${PREMIUM_DASH_TABS_LIMIT} dashboards.`;

	const plan = isPremium ? $format('label_premium') : $format('label_basic_plan');

	let dashToDelete = null;
	let editDashId: string | null = null;
	const { loading: deleteLoading, run: runDelete } = createAsyncAction();
	const { loading: isSaving, run: runSave } = createAsyncAction();

	export let dashboardsEdit = false;
	export let editDashObj: any = {
		name: '',
		icon: DEFAULT_DASH_ICON,
		backgroundColor: '',
	};
	export let showDeleteModal = false;
	export let isEditDash = false;

	$: if (isEditDash || showDeleteModal) {
		const parts = $location.split('/');
		editDashId = parts[parts.length - 1];

		const dashToEdit = $dashboards[editDashId];

		if (dashToEdit) {
			if (showDeleteModal) {
				dashToDelete = dashToEdit;
			}

			// Same fallback as the tabs rail: missing icon → default column icon.
			const avatar = mapDashToAvatar(dashToEdit);
			editDashObj = {
				name: dashToEdit.name,
				icon: dashToEdit.icon?.trim() || avatar.icon || DEFAULT_DASH_ICON,
				backgroundColor: dashToEdit.backgroundColor?.trim() || '',
			};
		}
	}

	$: disableAdd = !editDashObj?.icon?.trim() || !editDashObj?.name?.trim();

	interface DashboardTab extends TypeTab {
		isDndShadowItem?: boolean;
	}

	let items: DashboardTab[];
	$: items = [...$dashboardTabs].filter((tab) => !checkIsEventLogTab(tab));

	$: listNewValues = items.reduce((acc, curr) => ((acc[curr.id] = curr.text), acc), {});

	const ICONS_DB = [
		{ id: 0, icon: DEFAULT_DASH_ICON, type: 'icon', isIcon: true },
		{ id: 1, icon: 'fat fa-microchip', type: 'icon', isIcon: true },
		{ id: 2, icon: 'fat fa-bolt', type: 'icon', isIcon: true },
		{ id: 3, icon: 'fat fa-garage', type: 'icon', isIcon: true },
		{ id: 4, icon: 'fat fa-server', type: 'icon', isIcon: true },
		{ id: 5, icon: 'fat fa-oven', type: 'icon', isIcon: true },
	];

	// Keep the picker highlight in sync with the active icon (defaults to the
	// same column icon the tabs rail uses when a dashboard has no icon set).
	$: icons = ICONS_DB.map((item) => ({
		...item,
		selected: item.icon === (editDashObj?.icon || DEFAULT_DASH_ICON),
	}));

	const onCloseDeleteModal = () => {
		dashToDelete = -1;
		showDeleteModal = false;
	};

	const onCloseModal = () => {
		dashboardsEdit = false;
		listNewValues = items.reduce((acc, curr) => ((acc[curr.id] = curr.text), acc), {});
		editDashId = null;
		dispatch('close');
	};

	const onDeleteCard = () =>
		runDelete(async () => {
			if ($location.includes('/dash/' + dashToDelete.id)) {
				replace(`/dash/${DEFAULT_DASH_ID}`);
			}
			await deleteDashboard(dashToDelete.id);
			onCloseDeleteModal();
			dashToDelete = null;
		});

	const onSave = () =>
		runSave(async () => {
			const visual = {
				icon: editDashObj.icon?.trim() || DEFAULT_DASH_ICON,
				backgroundColor: editDashObj.backgroundColor?.trim() || '',
			};
			let id;
			if (isEditDash) {
				id = editDashId;
				await requestChangeDashboardName(id, editDashObj.name, visual);
			} else {
				id = await addDashboard(editDashObj.name.trim(), visual);
			}

			editDashObj = {
				name: '',
				icon: DEFAULT_DASH_ICON,
				backgroundColor: '',
			};

			const currentDash = $dashboards[id];
			// Default widgets should be created for initial default created dashboard.
			if (!currentDash?.dashWidgets && id?.toString() === DEFAULT_DASH_ID.toString()) {
				await createNewDashWithDefaultWidgets(currentDash, id);
			}

			push(`/dash/${id}`);
			onCloseModal();
		});

	const onInputName = (event: InputEvent) => {
		const target = event.target as HTMLInputElement;
		editDashObj = {
			...editDashObj,
			name: target.value,
		};
	};

	const onSelectImage = (e: CustomEvent) => {
		editDashObj = {
			...editDashObj,
			icon: e.detail.icon || editDashObj.icon || DEFAULT_DASH_ICON,
			backgroundColor: e.detail.backgroundColor ?? editDashObj.backgroundColor ?? '',
		};
	};
</script>

<!-- Ivo: New Translation for title -->
<Modal
	on:close={onCloseModal}
	showModal={dashboardsEdit}
	title={isEditDash
		? $format('modal_title_edit_dashboard', { default: 'Edit Dashboard' })
		: $format('modal_title_add_dashboard', { default: 'Add New Dashboard' })}
	contentDirection="row"
>
	<svelte:fragment slot="content">
		<!-- {#if items.length >= DASHBOARDS_LIMIT}
			<Alert
				customIcon={!isPremium ? 'fas fa-gem icon-premium-color' : ''}
				type="warning"
				heading={$format(alertMessegaLimitTranslation, {
					values: {
						limit: DASHBOARDS_LIMIT,
						premiumLimit: PREMIUM_DASH_TABS_LIMIT,
					},
					default: alertMessageReachedLimitDefault,
				})}
				class="mb-l"
				hideSlotBottom={isPremium}
			>
				<Button
					slot="bottom"
					variant="premium"
					size="m"
					width="auto"
					on:click={() => {
						onCloseModal();
						push('/settings/premium');
					}}
				>
					{capFirstChar(
						$format('button_get_premium', {
							default: 'Get Premium',
						}),
					)}
				</Button>
			</Alert>
		{:else}
			<Alert
				class="mb-l"
				heading={$format('alert_heading_dashboard_created', {
					values: { limit: DASHBOARDS_LIMIT, count: items.length },
					default: `Dashboards created: ${items.length}/${DASHBOARDS_LIMIT}`,
				})}
				message={$format('alert_message_dashboard_created', {
					values: {
						limit: DASHBOARDS_LIMIT,
						plan: plan,
					},
					default: `${plan} allows to create up to ${DASHBOARDS_LIMIT} dashboards.`,
				})}
				isHeadingBold
			/>
		{/if} -->

		<Container noScroll padding="0" class="mb-m">
			<Text class="mb-s text-capitalize text-500" element="h2">
				{$format('label_menu_items_dashboard', { default: 'dashboard' })}&nbsp;{$format('name', {
					default: 'name',
				})}
			</Text>

			<Input
				class="mr-s"
				label={`${$format('label_menu_items_dashboard', { default: 'dashboard' })} ${$format(
					'name',
					{
						default: 'name',
					},
				)}`}
				icon={'fas fa-objects-column'}
				value={editDashObj.name}
				id="dashboard-tab-name-input"
				on:input={onInputName}
				aria-labelledby="dashboard-tab-name-input"
			/>
		</Container>

		<!-- <hr class="mt-l mb-l" /> -->

		<Container noScroll padding="0">
			<Text element="h2" class="mb-m text-500">
				{$format('select_image', { default: 'Select image' })}
			</Text>

			<ImageList
				selectedBackgroundColor={editDashObj.backgroundColor}
				selectedIcon={editDashObj.icon}
				iconsDB={icons}
				requestType="dashboard"
				showUpload={false}
				showTypeImages={false}
				showOtherImages={false}
				showIcons={true}
				showColors={true}
				coexistIconAndBackgroundColor={true}
				restrictColorsToPreviewImages={true}
				{backgroundColorPreviewImages}
				on:select-image={onSelectImage}
			/>
		</Container>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={onCloseModal}
		>
			{$format('button_cancel')}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{
				sm: 'm',
			}}
			disabled={(!isEditDash && items.length >= DASHBOARDS_LIMIT) || disableAdd}
			loading={$isSaving}
			on:click={(isEditDash || items.length <= DASHBOARDS_LIMIT) && onSave}
		>
			{$format('button_save')}
		</Button>
	</div>
</Modal>

<Modal
	on:close={onCloseDeleteModal}
	showModal={showDeleteModal}
	title={$format('prompt_title_delete_dashboard', {
		default: 'Delete Dashboard?',
	})}
>
	<Container slot="content" padding="0">
		<div class="d-flex flex-direction-column align-items-center">
			<Icon size="xxl" icon="fa-light fa-trash" class="text-danger mb-xs" />
			<Text class="text-bold text-center mb-s" styleType="controls-large">
				{dashToDelete.name}
			</Text>
			<Text class="width-full">
				{$format('on_delete_item_msg', {
					values: {
						device_name: dashToDelete.name,
						type: $format('label_menu_items_dashboard', {
							default: 'Dashboard',
						}),
					},
					default: `Deleting ${$format('label_menu_items_dashboard', {
						default: 'Dashboard',
					}).toLowerCase()} ‘${dashToDelete.name}’ will permanently remove it from your account.`,
				})}
			</Text>
		</div>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={onCloseDeleteModal}
			aria-label={$format('aria_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="danger"
			responsiveSize={{
				sm: 'm',
			}}
			loading={$deleteLoading}
			on:click={onDeleteCard}
			aria-label={$format('button_delete_dashboard', { default: 'Delete dashboard' })}
		>
			{$format('button_delete_dashboard', { default: 'Delete dashboard' })}
		</Button>
	</div>
</Modal>
